import { toast } from "react-toastify"
import React, { useRef, useState } from "react"

import { ASSETS } from "../../api/Registry";
import { TextAttribute } from "./TextAttribute";
import { DateAttribute } from "./DateAttribute";
import { ChoiceAttribute } from "./ChoiceAttribute";
import { APPLICATION } from "../../utils/Constants";
import { SectionAttribute } from "./SectionAttribute"
import { RouteHydration } from "../../utils/Helpers";
import HttpServices from "../../services/HttpServices";
import { TwoToneModal } from "../../lib/fragments/TwoToneModal"

export interface AttributeRef {
    integrityCheck: (e: any) => {
        passedValidation: boolean,
        attributes: any
    };
}

export const AddAttribute = ({ show, showOrHide, type, category, onComplete }) => {
    const [state, setstate] = useState({
        posting: false,
        attribute: null,
        whatToAdd: null,
        httpStatus: 200,
    })

    const whatToAddRef = useRef<AttributeRef | null>(null);

    const whatToAddList = [
        { name: "Section", icon: "fa-block-question", description: "Group related fields" },
        { name: "Text Input", icon: "fa-text-size", description: "Single or multi-line textual info" },
        { name: "Date Input", icon: "fa-calendar", description: "Date specific inputs" },
        { name: "Choice Input", icon: "fa-ballot-check", description: "Dropdowns, checkboxes, or radio buttons" },
        { name: "Asset Link", icon: "fa-link", description: "Link existing assets for dynamic references" },
    ]

    const selectWhatToAdd = (itemToAdd: any) => {
        if (!state.posting) {
            setstate((prev) => ({
                ...prev,
                whatToAdd: itemToAdd
            }))
        }
    }

    const backToWhatToAddList = () => {
        if (!state.posting) {
            setstate((prev) => ({
                ...prev,
                whatToAdd: null
            }))
        }
    }

    const loadWhatToAdd = (whatToAdd: any) => {
        switch (whatToAdd.name) {
            case "Section":
                return <SectionAttribute
                    ref={whatToAddRef}
                    whatToAdd={whatToAdd}
                    posting={state.posting}
                />

            case "Text Input":
                return <TextAttribute
                    ref={whatToAddRef}
                    whatToAdd={whatToAdd}
                    posting={state.posting}
                />

            case "Date Input":
                return <DateAttribute
                    ref={whatToAddRef}
                    whatToAdd={whatToAdd}
                    posting={state.posting}
                />

            case "Choice Input":
                return <ChoiceAttribute
                    ref={whatToAddRef}
                    whatToAdd={whatToAdd}
                    posting={state.posting}
                />

            default:
                return null
        }
    }

    const onFormSubmitHandler = (e: any) => {
        e.preventDefault()
        let { posting } = state

        if (!posting) {
            let { whatToAdd } = state

            if (!whatToAdd) {
                toast.error("Select an attribute to add", {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                })

                return
            }

            if (whatToAddRef.current) {
                let attributeType = null
                let attrRefResponse: any = null

                switch (whatToAdd.name) {
                    case 'Section':
                        attributeType = 'section'
                        attrRefResponse = whatToAddRef.current.integrityCheck(e);
                        break;

                    case 'Text Input':
                        attributeType = 'text_field'
                        attrRefResponse = whatToAddRef.current.integrityCheck(e);
                        break;

                    case 'Date Input':
                        attributeType = 'date_picker'
                        attrRefResponse = whatToAddRef.current.integrityCheck(e);
                        break;

                    case 'Choice Input':
                        attributeType = 'choice_control'
                        attrRefResponse = whatToAddRef.current.integrityCheck(e);
                        break;

                    default:
                        break;
                }

                if (attrRefResponse.passedValidation) {
                    setstate((prev) => ({
                        ...prev,
                        posting: true
                    }))

                    addAttributeToAssetForm(attributeType, attrRefResponse.attributes)
                }
            }
        }
    }

    const addAttributeToAssetForm = async (attributeType: any, attributes: any) => {
        let { httpStatus } = state

        try {
            let response: any = null
            let addAttributeRoute = null
            let formData = new FormData()

            formData.append('type', attributeType)
            formData.append('props', JSON.stringify(attributes))

            addAttributeRoute = RouteHydration(ASSETS.FORM_ATTRIBUTES, ':category', category.uuid)
            addAttributeRoute = RouteHydration(addAttributeRoute, ':type', type.uuid)
            response = await HttpServices.httpPost(addAttributeRoute, formData)

            if (httpStatus === 200) {
                const onCompletePayload = {
                    type: attributeType,
                    attr: attributes
                }

                onComplete(onCompletePayload)
            } else {
                if (response.status === 422) {
                    let { errors }: any = null
                    Object.entries(response.data).forEach(([field, errorMessages]) => {
                        const submissionErrors = {
                            ...errors,
                            [field]: errorMessages
                        };

                        setstate((prev) => ({
                            ...prev,
                            errors: submissionErrors,
                        }));
                    });
                } else {
                    toast.error(APPLICATION.ERR_MSG, {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                }
            }
        } catch (error) {
            httpStatus = 500
        }

        setstate((prevState) => ({
            ...prevState, httpStatus, posting: !prevState.posting
        }));
    }

    return (
        <React.Fragment>
            <TwoToneModal
                size={"3xl"}
                show={show}
                status={'fulfilled'}
                posting={state.posting}
                showOrHide={showOrHide}
                formAction={"Add Attribute"}
                onSubmit={onFormSubmitHandler}
                header={
                    <>
                        <div className="w-full flex flex-col h-full gap-y-2.5 px-2">
                            <div className="flex md:pb-0 pb-3 md:pt-2 pt-4 flex-row-reverse align-middle items-center">
                                <button type="button" className="rounded-md md:hidden text-gray-800 dark:text-gray-200 hover:text-gray-800 focus:outline-none flex flex-row align-middle pr-3"
                                    onClick={showOrHide}>
                                    <span className="fas fa-times border-none text-lg"></span>
                                    <span className="sr-only text-sm ml-3">Close panel</span>
                                </button>

                                <div className="flex-grow">
                                    <h2 className="md:text-lg text-xl font-medium tracking-wider text-slate-600 dark:text-white md:text-slate-600 dark:md:text-white">
                                        Add an Attribute
                                    </h2>
                                </div>
                            </div>

                            <div className="sm:col-span-8 flex-grow lg:col-span-7 mb-3 flex flex-col gap-y-3 text-primary dark:text-slate-100 md:text-slate-600 dark:md:text-slate-400">
                                <span className="block md:py-2 text-sm">
                                    Tailor fields to specific assets and link them with other existing assets for more detailed tracking.
                                </span>
                            </div>
                        </div>
                    </>
                }
                contents={
                    <>
                        <div className="w-full md:px-4 px-3">
                            {
                                !state.whatToAdd ? (
                                    <>
                                        <p className="text-sm mb-3">
                                            What would like to add?
                                        </p>

                                        <ul className={`w-full space-y-3 text-slate-500 dark:text-slate-400 transition-transform duration-700 ease-in-out`}>
                                            {
                                                whatToAddList.map((toAdd: any) => (
                                                    <button key={toAdd.icon} onClick={() => selectWhatToAdd(toAdd)} type="button" className="w-full flex items-center text-start gap-x-3 hover:bg-slate-100 dark:hover:bg-gray-700 ring-1 ring-slate-900/15 dark:ring-slate-200/35 rounded-md md:py-2 py-3 px-4">
                                                        <div className="flex-none w-9 text-slate-900 dark:text-white md:flex hidden">
                                                            <span className={`fa-light ${toAdd.icon} fa-xl`}></span>
                                                        </div>

                                                        <div className="flex-grow">
                                                            <div className="text-sm md:block text-slate-900 dark:text-white flex items-center gap-x-2">
                                                                <span className="md:hidden w-9">
                                                                    <span className={`fa-light ${toAdd.icon} fa-xl`}></span>
                                                                </span>
                                                                {toAdd.name}
                                                            </div>

                                                            <span className="text-sm md:block hidden">
                                                                {toAdd.description}
                                                            </span>
                                                        </div>
                                                    </button>
                                                ))
                                            }
                                        </ul>
                                    </>
                                ) : (
                                    <div className="w-full px-2">
                                        <button type="button" onClick={backToWhatToAddList} className="flex flex-row items-center gap-x-3 align-middle pb-1.5 text-sm text-theme dark:text-theme hover:text-secondary">
                                            <i className="fa-duotone fa-arrow-left fa-lg"></i>
                                            Back
                                        </button>

                                        {
                                            loadWhatToAdd(state.whatToAdd)
                                        }
                                    </div>
                                )
                            }
                        </div>
                    </>
                }
            />
        </React.Fragment>
    )
}